// Stats (phase 16) — no mockup; built in the established visual language.
// Goals strip (monthly revenue / quarterly revenue / monthly leads, inline-
// editable targets), revenue-vs-expenses month bars, lead pipeline, project
// types, avg project value by FY. All aggregation comes from
// /api/admin/stats; goal revenue targets are ALWAYS ex GST (Henry quotes
// ex); chart money respects the Settings money toggle.
//
// EOFY "wrapped" snapshot (Phase 2.6, mockup #9): a year-in-review section
// behind its own FY chip, independent of the tabs/date-range above. Every
// figure comes from the SAME /api/admin/stats round trip (a `wrapped`
// sub-object keyed by a `wrapped_fy` query param) — always ex GST, no money-
// mode toggle, matching the Goals strip's convention above.

import { useViewState } from '../lib/useViewState';
import { oneOf, matching } from '../lib/viewState';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoalCopyButton } from '../components/GoalCopyButton';
import { keepGoal } from '../lib/goalActions';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { useMoneyMode } from '../lib/money';
import { compactMoney, goalHintOf } from '../lib/statsMobile';
import { paceOf } from '../lib/targets';
import { useIsMobile } from '../lib/useIsMobile';
import { calendarMonthName, melbourneToday } from '../../shared/dates';
import { quarterOf } from '../../shared/finance';
import { fyKeyOf, fyMonths, monthLabel, type LeadAcquisitionStats } from '../../shared/stats';
import {
  BRANDS,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
  type Brand,
  type DocumentSendListItem,
  type LeadSource,
  type LeadStatus,
} from '../../shared/types';
import { HBars, MonthBars } from '../components/charts';
import { DateSheet } from '../components/DateSheet';
import { DocumentSendForm } from '../components/DocumentSendForm';
import { DocumentSendsBand } from '../components/DocumentSendsBand';
import { PlusIcon } from '../components/icons';
import { FieldRow } from '../components/FieldSheet';
import {
  DateField,
  Dropdown,
  formatDate,
  formatMoney,
  InlineMoney,
  InlineText,
  PageHeader,
  Tabs,
  Vital,
  Vitals,
} from '../components/primitives';

interface StatsPayload {
  from: string;
  to: string;
  current_fy: string;
  months: {
    month: string;
    revenue_cents: number;
    revenue_ex_cents: number;
    revenue_by_brand: Record<Brand, {
      revenue_cents: number;
      revenue_ex_cents: number;
    }>;
    expenses_cents: number;
    expenses_ex_cents: number;
  }[];
  leads: {
    total: number;
    by_status: Partial<Record<LeadStatus, number>>;
    by_source: Partial<Record<string, number>>;
    conversion: number | null;
  };
  /** Round three §4 — same window as `leads`; optional so an older Worker still renders. */
  lead_acquisition?: LeadAcquisitionStats;
  project_types: { type: string; count: number }[];
  avg_value_by_fy: { fy: string; avg_ex_cents: number; count: number }[];
  wrapped: {
    fy: string;
    prior_fy: string | null;
    available_fys: string[];
    revenue_ex_cents: number;
    prior_revenue_ex_cents: number;
    revenue_change_pct: number | null;
    avg_project_value_ex_cents: number | null;
    projects_delivered: number;
    avg_days_lead_to_won: number | null;
    avg_days_shoot_to_delivered: number | null;
    revenue_by_client: { client_id: string; client_name: string; revenue_ex_cents: number }[];
    months: { month: string; count: number }[];
    project_count_by_type: { type: string; count: number }[];
  };
  goals: {
    month: {
      period: string;
      period_key: string;
      revenue: { carried_forward: boolean; source_period_key: string | null; target_ex_cents: number | null; actual_ex_cents: number };
      leads: { carried_forward: boolean; source_period_key: string | null; target: number | null; actual: number };
      proposals_delivered: { carried_forward: boolean; source_period_key: string | null; target: number | null; actual: number };
      jobs_won: { carried_forward: boolean; source_period_key: string | null; target: number | null; actual: number };
    };
    quarter: {
      period: string;
      period_key: string;
      revenue: { carried_forward: boolean; source_period_key: string | null; target_ex_cents: number | null; actual_ex_cents: number };
      leads: { carried_forward: boolean; source_period_key: string | null; target: number | null; actual: number };
      proposals_delivered: { carried_forward: boolean; source_period_key: string | null; target: number | null; actual: number };
      jobs_won: { carried_forward: boolean; source_period_key: string | null; target: number | null; actual: number };
    };
  };
}

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function monthPeriodLabel(period: string): string {
  return `${MONTHS_FULL[Number(period.slice(5, 7)) - 1]} ${period.slice(0, 4)}`;
}

/** "$84k" — compact axis money. */
function axisMoney(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) return `$${Math.round(dollars / 1000)}k`;
  return `$${Math.round(dollars)}`;
}

// ── Goal target editors — same InlineMoney/InlineText interaction the
//    revenue/leads cards have always used, extended with a "click to add"
//    display in place of a bare "—" when nothing's set yet (mockup §1a). ──
const CLICK_TO_ADD = (
  <span className="dim" style={{ color: 'var(--text-faint)' }}>
    — click to add
  </span>
);

function moneyTargetEditor(
  cents: number | null,
  onSave: (cents: number | null) => void,
  isMobile: boolean,
  label: string,
  context: string,
) {
  // An unset target falls through to Phase 0's own faint "Add" on mobile
  // (InlineMoney maps a bare em dash to it) — the same empty-field language
  // every other tappable value on a phone uses.
  return (
    <InlineMoney
      cents={cents}
      valueClass=""
      emptyLabel={isMobile ? '—' : '— click to add'}
      label={label}
      context={context}
      onSave={onSave}
    />
  );
}

function countTargetEditor(
  value: number | null,
  onSave: (n: number | null) => void,
  isMobile: boolean,
  label: string,
) {
  return (
    <InlineText
      value={value === null ? null : String(value)}
      mono
      kind="number"
      label={label}
      placeholder="target"
      // Mobile leaves `display` unset so InlineText renders the same faint
      // "Add" the money editor beside it does.
      display={value === null && !isMobile ? CLICK_TO_ADD : undefined}
      onSave={(raw) => {
        const n = raw === null ? null : Number(raw.replace(/[^\d]/g, ''));
        onSave(n === null || Number.isNaN(n) ? null : n);
      }}
    />
  );
}

/** "FY26" → "FY 2025 / 26" — the wrapped chip's full label. */
function fyChipLabel(fyKey: string): string {
  const m = /^FY(\d{2})$/.exec(fyKey);
  if (!m) return fyKey;
  const endYear = 2000 + Number(m[1]);
  return `FY ${endYear - 1} / ${m[1]}`;
}

/** "FY26" → "FY25/26" — the compact form used inline in hint text. */
function fyShortLabel(fyKey: string): string {
  const m = /^FY(\d{2})$/.exec(fyKey);
  if (!m) return fyKey;
  const end = Number(m[1]);
  const start = String((end - 1 + 100) % 100).padStart(2, '0');
  return `FY${start}/${m[1]}`;
}

const STATUS_ORDER: LeadStatus[] = [
  'enquiry', 'discovery_call', 'proposal_creation', 'proposal_sent', 'negotiation',
  'locked_in', 'won', 'lost', 'ghosted', 'postponed', 'void',
];

/** Paid vs organic (feedback round three §4) — sits under Lead pipeline.
    Locked in counts as won for conversion; revenue lands on Convert. */
function PaidOrganic({ data, windowLabel, isMobile }: {
  data: LeadAcquisitionStats;
  windowLabel: string;
  isMobile: boolean;
}) {
  const rows = [
    { key: 'paid', label: 'Paid', ...data.paid },
    { key: 'organic', label: 'Organic', ...data.organic },
  ] as const;
  const total = rows.reduce((sum, r) => sum + r.enquiries, 0);
  return (
    <div className="group po-group">
      <div className="group-head">
        <span className="group-title">Paid vs organic</span>
        <span className="group-count">{windowLabel} · by acquisition</span>
        <span className="group-link" style={{ cursor: 'default' }}>{data.untagged} untagged</span>
      </div>
      <div className="po-table" role="table">
        <div className="po-row head" role="row">
          <span /><span>Enquiries</span><span>Won</span><span>Conversion</span><span>Revenue{isMobile ? '' : ' · ex GST'}</span>
        </div>
        {rows.map((r) => {
          const pct = r.conversion === null ? null : Math.round(r.conversion * 100);
          return (
            <div key={r.key} className="po-row" role="row">
              <span className="po-label"><span className={`po-dot ${r.key}`} />{r.label}</span>
              <span className="n">{r.enquiries}<small>{total ? Math.round((r.enquiries / total) * 100) : 0}%</small></span>
              <span className="n">{r.won + r.locked_in}{r.locked_in > 0 && <small title="Locked in counts as a win for conversion">{r.locked_in} locked</small>}</span>
              <span className="n">{pct === null ? '—' : `${pct}%`}<span className="goal-bar po-bar"><span className="fill-accent" style={{ width: `${pct ?? 0}%` }} /></span></span>
              <span className="n gold">{formatMoney(r.revenue_cents)}<small>{r.converted} converted</small></span>
            </div>
          );
        })}
      </div>
      <p className="po-note">Locked in counts as won for conversion; revenue lands when the lead converts. Spend and cost per lead arrive with the Marketing tab.</p>
    </div>
  );
}

// ── Period presets (Henry's filter ask, 2026-06-13): quarter / FY /
//    last FY / custom date range ─────────────────────────────────────────

type Preset = 'quarter' | 'fy' | 'lastfy' | 'custom';
type BrandFilter = 'all' | Brand;

const PRESET_TABS: readonly { value: Preset; label: string }[] = [
  { value: 'quarter', label: 'This quarter' },
  { value: 'fy', label: 'This FY' },
  { value: 'lastfy', label: 'Last FY' },
  { value: 'custom', label: 'Custom' },
];

const BRAND_TABS: readonly { value: BrandFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'hsp', label: 'HSP' },
  { value: 'ss', label: 'SS' },
];

function lastDayOfMonth(monthKey: string): string {
  const d = new Date(`${monthKey}-01T00:00:00`);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fySpan(fyKey: string): { from: string; to: string } {
  const months = fyMonths(fyKey);
  return { from: `${months[0]}-01`, to: lastDayOfMonth(months[11]) };
}

function presetWindow(preset: Preset, today: string): { from: string; to: string; label: string } {
  const fy = fyKeyOf(today)!;
  if (preset === 'quarter') {
    const q = quarterOf(today)!;
    const startMonth = q.start.slice(0, 7);
    const endKey = (() => {
      const d = new Date(`${startMonth}-01T00:00:00`);
      d.setMonth(d.getMonth() + 2);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();
    return { from: q.start, to: lastDayOfMonth(endKey), label: q.key };
  }
  if (preset === 'lastfy') {
    const lastFY = `FY${String(Number(fy.slice(2)) - 1).padStart(2, '0')}`;
    return { ...fySpan(lastFY), label: lastFY };
  }
  return { ...fySpan(fy), label: fy };
}

export const statsViewCodec = {
  preset: oneOf('range', ['quarter', 'fy', 'lastfy', 'custom'], 'fy'),
  customFrom: matching('from', /^\d{4}-\d{2}-\d{2}$/), customTo: matching('to', /^\d{4}-\d{2}-\d{2}$/),
  wrappedFy: matching('fy', /^FY\d{2}$/), brandFilter: oneOf('brand', ['all', ...BRANDS], 'all'),
};

export default function Stats() {
  const { initial, publish } = useViewState(statsViewCodec, state => {
    setPreset(state.preset);
    setCustomFrom(state.customFrom);
    setCustomTo(state.customTo);
    setWrappedFy(state.wrappedFy);
    setBrandFilter(state.brandFilter);
  });
  const [data, setData] = useState<StatsPayload | null>(null);
  const [preset, setPreset] = useState<Preset>(() => initial.preset);
  const today = melbourneToday();
  const [customFrom, setCustomFrom] = useState<string | null>(() => initial.customFrom);
  const [customTo, setCustomTo] = useState<string | null>(() => initial.customTo);
  const isMobile = useIsMobile();
  // Which end of the custom range the mobile date sheet is editing.
  const [customSheet, setCustomSheet] = useState<'from' | 'to' | null>(null);
  const mode = useMoneyMode();
  // EOFY wrapped's own FY switcher — null until the server resolves a
  // default (the currently-open FY), decoupled from the tabs/range above.
  const [wrappedFy, setWrappedFy] = useState<string | null>(() => initial.wrappedFy);
  const [fyPickerOpen, setFyPickerOpen] = useState(false);
  const [brandFilter, setBrandFilter] = useState<BrandFilter>(() => initial.brandFilter);

  useEffect(() => {
    publish({ preset, customFrom, customTo, wrappedFy, brandFilter });
  }, [publish, preset, customFrom, customTo, wrappedFy, brandFilter]);

  const window_ = useMemo(() => {
    if (preset === 'custom') {
      const from = customFrom ?? presetWindow('fy', today).from;
      const to = customTo ?? today;
      return { from, to, label: `${formatDate(from)} – ${formatDate(to)}` };
    }
    return presetWindow(preset, today);
  }, [preset, customFrom, customTo, today]);

  // One fetch at a time wins (a period chip tapped twice in a row must not
  // land the slower answer), and mutations made elsewhere — a goal target
  // edited from the FAB quick-add sheet or another tab — arrive as
  // `ss:mutated` coalesced at 250ms, the pattern every page phase uses.
  const seq = useRef(0);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Proposals & quotes band (workflow D1): the sends in the same window,
  // fetched alongside the stats so they share every refresh trigger.
  const [sends, setSends] = useState<DocumentSendListItem[] | null>(null);
  const [sendsFailed, setSendsFailed] = useState(false);
  // `null` = closed; `{ send: null }` = a fresh Log sent.
  const [sendForm, setSendForm] = useState<{ send: DocumentSendListItem | null } | null>(null);

  const fetchStats = useCallback(() => {
    const params = new URLSearchParams({ from: window_.from, to: window_.to });
    if (wrappedFy) params.set('wrapped_fy', wrappedFy);
    const mine = ++seq.current;
    const range = new URLSearchParams({ from: window_.from, to: window_.to });
    // Resolve both together so the page (and the Shell's back-navigation
    // scroll restore) sees one layout, not a band that grows after the
    // stats landed. A failed sends fetch keeps the last list and flags it
    // rather than masquerading as "nothing sent".
    const sendsReq = api<{ sends: DocumentSendListItem[] }>(`/api/admin/document-sends?${range}`).then(
      (payload) => ({ ok: true as const, sends: payload.sends }),
      () => ({ ok: false as const }),
    );
    Promise.all([api<StatsPayload>(`/api/admin/stats?${params}`), sendsReq])
      .then(([payload, sendsResult]) => {
        if (mine !== seq.current) return;
        setData(payload);
        if (sendsResult.ok) setSends(sendsResult.sends);
        setSendsFailed(!sendsResult.ok);
      })
      .catch(() => {});
  }, [window_.from, window_.to, wrappedFy]);

  // The page's own saves refresh at once and cancel any pending coalesced
  // fetch, so a PUT costs one round trip rather than two.
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchStats();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchStats, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      seq.current++;
    };
  }, [fetchStats]);

  const saveGoal = async (
    period: string,
    kind: 'revenue' | 'leads' | 'proposals_delivered' | 'jobs_won',
    target: number | null,
  ) => {
    const metric = kind === 'revenue' ? 'revenue_ex' : kind;
    await api('/api/admin/stats/goals', { method: 'PUT', json: { period_key: period, metric, target } })
      .catch((error) => reportError(error, 'Could not save the goal.'));
    refresh();
  };

  if (!data) return <div className="page-body" />;

  // Glance money (§11.1 A/C): a 176px card can't hold "$12,450 of $40,000" at
  // 26px display type, so the cards, the wrapped stats and the money bars
  // round to "$12.5k" on a phone. The vitals keep the exact figures.
  const cardMoney = (cents: number | null) => (isMobile ? compactMoney(cents) : formatMoney(cents));

  const rev = (m: StatsPayload['months'][number]) => {
    const source = brandFilter === 'all' ? m : m.revenue_by_brand[brandFilter];
    return mode === 'ex' ? source.revenue_ex_cents : source.revenue_cents;
  };
  const allRevenue = (m: StatsPayload['months'][number]) => (mode === 'ex' ? m.revenue_ex_cents : m.revenue_cents);
  const exp = (m: StatsPayload['months'][number]) => (mode === 'ex' ? m.expenses_ex_cents : m.expenses_cents);
  const fyRevenue = data.months.reduce((s, m) => s + rev(m), 0);
  const allFyRevenue = data.months.reduce((s, m) => s + allRevenue(m), 0);
  const fyExpenses = data.months.reduce((s, m) => s + exp(m), 0);
  const brandLabel = brandFilter === 'all' ? 'All brands' : brandFilter.toUpperCase();
  const currentMonthKey = data.goals.month.period;

  const statusItems = STATUS_ORDER.filter((s) => (data.leads.by_status[s] ?? 0) > 0).map((s) => ({
    label: LEAD_STATUS_LABELS[s],
    value: data.leads.by_status[s] ?? 0,
  }));
  const sourceItems = Object.entries(data.leads.by_source)
    .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
    .map(([source, count]) => ({
      label: LEAD_SOURCE_LABELS[source as LeadSource] ?? source,
      value: count ?? 0,
    }));

  // ── EOFY wrapped (Phase 2.6) — busiest month is whichever bucket peaks;
  //    ties keep the first (earliest) month, same as a plain scan would.
  const wrapped = data.wrapped;
  const maxMonthCount = Math.max(0, ...wrapped.months.map((m) => m.count));
  const busiestIdx = maxMonthCount > 0 ? wrapped.months.findIndex((m) => m.count === maxMonthCount) : -1;
  const revenueChangeTone = wrapped.revenue_change_pct === null || wrapped.revenue_change_pct >= 0 ? 'accent' : undefined;

  // Band head on a phone: "FY27 · Jul – Oct" — the window's months up to
  // today (a custom range already reads as its own dates).
  const sendsRangeEnd = window_.to < today ? window_.to : today;
  const mobileSendsLabel =
    preset === 'custom'
      ? window_.label
      : `${window_.label} · ${monthLabel(window_.from.slice(0, 7))} – ${monthLabel(sendsRangeEnd.slice(0, 7))}`;

  return (
    <>
      <PageHeader
        title="Stats"
        mobileActions={[
          { key: 'add', label: 'Log a sent proposal or quote', icon: <PlusIcon />, onPress: () => setSendForm({ send: null }) },
        ]}
      />
      {/* ≤880px (§11.1 A): the period control is the chip row `Tabs` already
          renders, sitting above the scroll area with Custom's two date rows
          under it. Every band below names its own period, so the toolbar's
          trailing range label has nothing left to say here. */}
      {isMobile ? (
        <>
          <Tabs options={PRESET_TABS} value={preset} onChange={(p) => setPreset(p)} />
          {preset === 'custom' && (
            <>
              <div className="stats-custom">
                <FieldRow label="From" value={formatDate(window_.from)} mono onPress={() => setCustomSheet('from')} />
                <FieldRow label="To" value={formatDate(window_.to)} mono onPress={() => setCustomSheet('to')} />
              </div>
              <DateSheet
                open={customSheet === 'from'}
                title="From"
                context="Custom period"
                value={window_.from}
                onPick={setCustomFrom}
                onClose={() => setCustomSheet(null)}
              />
              <DateSheet
                open={customSheet === 'to'}
                title="To"
                context="Custom period"
                value={window_.to}
                onPick={setCustomTo}
                onClose={() => setCustomSheet(null)}
              />
            </>
          )}
        </>
      ) : (
        <div className="toolbar">
          <Tabs options={PRESET_TABS} value={preset} onChange={(p) => setPreset(p)} />
          {preset === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <DateField value={customFrom ?? window_.from} onChange={setCustomFrom} placeholder="From…" />
              <span style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', fontSize: 10 }}>→</span>
              <DateField value={customTo ?? window_.to} onChange={setCustomTo} placeholder="To…" />
            </div>
          )}
          <span
            className="group-count"
            style={{ marginLeft: 'auto', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            {window_.label}
          </span>
        </div>
      )}

      <div className="page-body">
        <div className={`content wide${isMobile ? ' stats-m' : ''}`} style={{ paddingTop: 18 }}>
          {/* ── Goals (always ex GST — quotes are ex) ────────────────── */}
          <div className="group-head" style={{ border: 'none', padding: '0 0 9px' }}>
            <span className="group-title">This month</span>
            <span className="group-count">{currentMonthKey}</span>
            <GoalCopyButton periods={['month']} disabled={!Object.values(data.goals.month).some(goal => typeof goal === 'object' && goal.carried_forward)} onCopied={refresh} />
          </div>
          <div className="goal-grid cols-4">
            <GoalCard
              title="Revenue this month"
              shortTitle="Revenue"
              period={monthPeriodLabel(currentMonthKey)}
              periodKey={currentMonthKey}
              today={today}
              tone="gold"
              actualDisplay={cardMoney(data.goals.month.revenue.actual_ex_cents)}
              actual={data.goals.month.revenue.actual_ex_cents}
              target={data.goals.month.revenue.target_ex_cents}
              carriedFrom={data.goals.month.revenue.carried_forward ? data.goals.month.revenue.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.month.period, 'revenue_ex', data.goals.month.revenue.target_ex_cents!); refresh(); }}
              targetEditor={moneyTargetEditor(
                data.goals.month.revenue.target_ex_cents,
                (cents) => saveGoal(currentMonthKey, 'revenue', cents),
                isMobile,
                'Revenue target',
                `${monthPeriodLabel(currentMonthKey)} · ex GST`,
              )}
            />
            <GoalCard
              title="Proposals & quotes sent"
              shortTitle="Proposals & quotes"
              period={monthPeriodLabel(currentMonthKey)}
              periodKey={currentMonthKey}
              today={today}
              tone="accent"
              actualDisplay={String(data.goals.month.proposals_delivered.actual)}
              actual={data.goals.month.proposals_delivered.actual}
              target={data.goals.month.proposals_delivered.target}
              carriedFrom={data.goals.month.proposals_delivered.carried_forward ? data.goals.month.proposals_delivered.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.month.period, 'proposals_delivered', data.goals.month.proposals_delivered.target!); refresh(); }}
              targetEditor={countTargetEditor(
                data.goals.month.proposals_delivered.target,
                (n) => saveGoal(currentMonthKey, 'proposals_delivered', n),
                isMobile,
                'Proposals & quotes target',
              )}
            />
            <GoalCard
              title="Jobs won this month"
              shortTitle="Jobs won"
              period={monthPeriodLabel(currentMonthKey)}
              periodKey={currentMonthKey}
              today={today}
              tone="accent"
              actualDisplay={String(data.goals.month.jobs_won.actual)}
              actual={data.goals.month.jobs_won.actual}
              target={data.goals.month.jobs_won.target}
              carriedFrom={data.goals.month.jobs_won.carried_forward ? data.goals.month.jobs_won.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.month.period, 'jobs_won', data.goals.month.jobs_won.target!); refresh(); }}
              targetEditor={countTargetEditor(
                data.goals.month.jobs_won.target,
                (n) => saveGoal(currentMonthKey, 'jobs_won', n),
                isMobile,
                'Jobs won target',
              )}
            />
            <GoalCard
              title="New leads this month"
              shortTitle="New leads"
              period={monthPeriodLabel(currentMonthKey)}
              periodKey={currentMonthKey}
              today={today}
              tone="accent"
              actualDisplay={String(data.goals.month.leads.actual)}
              actual={data.goals.month.leads.actual}
              target={data.goals.month.leads.target}
              carriedFrom={data.goals.month.leads.carried_forward ? data.goals.month.leads.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.month.period, 'leads', data.goals.month.leads.target!); refresh(); }}
              targetEditor={countTargetEditor(
                data.goals.month.leads.target,
                (n) => saveGoal(currentMonthKey, 'leads', n),
                isMobile,
                'New leads target',
              )}
            />
          </div>

          <div className="group-head" style={{ border: 'none', padding: '22px 0 9px' }}>
            <span className="group-title">This quarter</span>
            <span className="group-count">{data.goals.quarter.period}</span>
            <GoalCopyButton periods={['quarter']} disabled={!Object.values(data.goals.quarter).some(goal => typeof goal === 'object' && goal.carried_forward)} onCopied={refresh} />
          </div>
          <div className="goal-grid cols-4">
            <GoalCard
              title="Revenue this quarter"
              shortTitle="Revenue"
              period={data.goals.quarter.period}
              periodKey={data.goals.quarter.period}
              today={today}
              tone="gold"
              actualDisplay={cardMoney(data.goals.quarter.revenue.actual_ex_cents)}
              actual={data.goals.quarter.revenue.actual_ex_cents}
              target={data.goals.quarter.revenue.target_ex_cents}
              carriedFrom={data.goals.quarter.revenue.carried_forward ? data.goals.quarter.revenue.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.quarter.period, 'revenue_ex', data.goals.quarter.revenue.target_ex_cents!); refresh(); }}
              targetEditor={moneyTargetEditor(
                data.goals.quarter.revenue.target_ex_cents,
                (cents) => saveGoal(data.goals.quarter.period, 'revenue', cents),
                isMobile,
                'Revenue target',
                `${data.goals.quarter.period} · ex GST`,
              )}
            />
            <GoalCard
              title="Proposals & quotes sent"
              shortTitle="Proposals & quotes"
              period={data.goals.quarter.period}
              periodKey={data.goals.quarter.period}
              today={today}
              tone="accent"
              actualDisplay={String(data.goals.quarter.proposals_delivered.actual)}
              actual={data.goals.quarter.proposals_delivered.actual}
              target={data.goals.quarter.proposals_delivered.target}
              carriedFrom={data.goals.quarter.proposals_delivered.carried_forward ? data.goals.quarter.proposals_delivered.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.quarter.period, 'proposals_delivered', data.goals.quarter.proposals_delivered.target!); refresh(); }}
              targetEditor={countTargetEditor(
                data.goals.quarter.proposals_delivered.target,
                (n) => saveGoal(data.goals.quarter.period, 'proposals_delivered', n),
                isMobile,
                'Proposals & quotes target',
              )}
            />
            <GoalCard
              title="Jobs won this quarter"
              shortTitle="Jobs won"
              period={data.goals.quarter.period}
              periodKey={data.goals.quarter.period}
              today={today}
              tone="accent"
              actualDisplay={String(data.goals.quarter.jobs_won.actual)}
              actual={data.goals.quarter.jobs_won.actual}
              target={data.goals.quarter.jobs_won.target}
              carriedFrom={data.goals.quarter.jobs_won.carried_forward ? data.goals.quarter.jobs_won.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.quarter.period, 'jobs_won', data.goals.quarter.jobs_won.target!); refresh(); }}
              targetEditor={countTargetEditor(
                data.goals.quarter.jobs_won.target,
                (n) => saveGoal(data.goals.quarter.period, 'jobs_won', n),
                isMobile,
                'Jobs won target',
              )}
            />
            <GoalCard title="New leads this quarter" shortTitle="New leads" period={data.goals.quarter.period}
              periodKey={data.goals.quarter.period} today={today} tone="accent"
              actualDisplay={String(data.goals.quarter.leads.actual)} actual={data.goals.quarter.leads.actual}
              target={data.goals.quarter.leads.target}
              carriedFrom={data.goals.quarter.leads.carried_forward ? data.goals.quarter.leads.source_period_key : null}
              onKeep={async () => { await keepGoal(data.goals.quarter.period, 'leads', data.goals.quarter.leads.target!); refresh(); }}
              targetEditor={countTargetEditor(data.goals.quarter.leads.target, (n) => saveGoal(data.goals.quarter.period, 'leads', n), isMobile, 'New leads target')} />
          </div>

          {/* ── Proposals & quotes (workflow D1) ─────────────────────── */}
          <DocumentSendsBand
            sends={sends}
            sendsFailed={sendsFailed}
            periodLabel={window_.label}
            mobileRangeLabel={mobileSendsLabel}
            onLogSent={() => setSendForm({ send: null })}
            onEdit={(send) => setSendForm({ send })}
          />

          {/* ── Revenue by month ─────────────────────────────────────── */}
          <div className="group">
            <div className="group-head">
              <span className="group-title">Revenue by month</span>
              {/* ≤880px (§11.1 B): the brand switch is its own chip row under
                  the band rather than a control squeezed into the band. */}
              {isMobile ? (
                <span className="group-count">{window_.label} · paid{mode === 'ex' ? ' · ex GST' : ''}</span>
              ) : (
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Tabs<BrandFilter>
                    options={BRAND_TABS}
                    value={brandFilter}
                    onChange={setBrandFilter}
                  />
                  <span className="group-count">{window_.label} · paid invoices{mode === 'ex' ? ' · ex GST' : ''}</span>
                </span>
              )}
            </div>
            {isMobile && (
              <div className="chiprow">
                {BRAND_TABS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`c${o.value === brandFilter ? ' on' : ''}`}
                    onClick={() => setBrandFilter(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
            <MonthBars
              data={data.months.map((m) => ({
                label: monthLabel(m.month),
                primary: rev(m),
                secondary: brandFilter === 'all' ? exp(m) : 0,
                primaryTitle: `${monthLabel(m.month)} ${brandLabel} revenue ${formatMoney(rev(m))}`,
                secondaryTitle: `${monthLabel(m.month)} expenses ${formatMoney(exp(m))}`,
                current: m.month === currentMonthKey,
              }))}
              formatAxis={axisMoney}
            />
            <div className="chart-legend">
              <span><span className="swatch gold" />{brandLabel} revenue</span>
              {brandFilter === 'all' && <span><span className="swatch dim" />Expenses</span>}
            </div>
            <Vitals>
              <Vital label={`${brandLabel} revenue`} value={formatMoney(fyRevenue)} tone="gold" sub={window_.label} />
              {brandFilter === 'all' ? (
                <>
                  <Vital label="Expenses" value={formatMoney(fyExpenses)} sub={window_.label} />
                  <Vital
                    label="Profit"
                    value={formatMoney(fyRevenue - fyExpenses)}
                    tone="accent"
                    sub="revenue − expenses"
                  />
                </>
              ) : (
                <>
                  <Vital label="All-brand revenue" value={formatMoney(allFyRevenue)} sub={window_.label} />
                  <Vital
                    label={`${brandLabel} share`}
                    value={allFyRevenue > 0 ? `${Math.round((fyRevenue / allFyRevenue) * 100)}%` : '—'}
                    tone="accent"
                    sub="of paid revenue"
                  />
                </>
              )}
              <Vital
                label="Avg paid month"
                value={formatMoney(Math.round(fyRevenue / Math.max(1, data.months.filter((m) => rev(m) > 0).length)))}
                sub="months with revenue"
              />
            </Vitals>
          </div>

          {/* ── Pipeline + project types two-up ──────────────────────── */}
          <div className="group two-up">
            <div>
              <div className="group-head">
                <span className="group-title">Lead pipeline</span>
                <span className="group-count">{window_.label} · {data.leads.total} leads</span>
                <span className="group-link" style={{ cursor: 'default' }}>
                  {data.leads.conversion === null
                    ? 'No decided leads'
                    : `${Math.round(data.leads.conversion * 100)}% conversion`}
                </span>
              </div>
              {statusItems.length > 0 ? (
                <HBars items={statusItems} />
              ) : (
                <div className="empty-note">No leads in this period.</div>
              )}
              {sourceItems.length > 0 && (
                <>
                  <div className="group-head" style={{ marginTop: 20 }}>
                    <span className="group-title">Sources</span>
                  </div>
                  <HBars items={sourceItems} />
                </>
              )}
            </div>
            <div>
              <div className="group-head">
                <span className="group-title">Project types</span>
                <span className="group-count">{window_.label}</span>
              </div>
              {data.project_types.length > 0 ? (
                <HBars items={data.project_types.map((t) => ({ label: t.type, value: t.count }))} />
              ) : (
                <div className="empty-note">No dated projects in this period.</div>
              )}
              <div className="group-head" style={{ marginTop: 20 }}>
                <span className="group-title">Avg project value · ex GST</span>
              </div>
              {data.avg_value_by_fy.length > 0 ? (
                <HBars
                  tone="gold"
                  items={data.avg_value_by_fy.map((row) => ({
                    label: row.fy,
                    value: row.avg_ex_cents,
                    display: cardMoney(row.avg_ex_cents),
                  }))}
                />
              ) : (
                <div className="empty-note">No project values yet.</div>
              )}
            </div>
          </div>

          {data.lead_acquisition && (
            <PaidOrganic data={data.lead_acquisition} windowLabel={window_.label} isMobile={isMobile} />
          )}

          {/* ── EOFY wrapped (Phase 2.6, mockup #9) ──────────────────── */}
          <div className="group">
            <div className="group-head">
              <span className="group-title">EOFY wrapped</span>
              <span className="group-count">Year in review</span>
              <div className="dropdown-anchor" style={{ marginLeft: 'auto' }}>
                {/* The chip is a real button on mobile — it is a 40px tap
                    target there, and Dropdown opens as a PickerSheet. */}
                {isMobile ? (
                  <button type="button" className="fy-chip" onClick={() => setFyPickerOpen(true)}>
                    {fyChipLabel(wrapped.fy)} <span className="caret">▾</span>
                  </button>
                ) : (
                  <span className="fy-chip" onClick={() => setFyPickerOpen((o) => !o)}>
                    {fyChipLabel(wrapped.fy)} <span className="caret">▾</span>
                  </span>
                )}
                {fyPickerOpen && (
                  <Dropdown
                    items={wrapped.available_fys.map((fy) => ({ value: fy, label: fyChipLabel(fy) }))}
                    value={wrapped.fy}
                    label="Financial year"
                    onSelect={(fy) => setWrappedFy(fy)}
                    onClose={() => setFyPickerOpen(false)}
                  />
                )}
              </div>
            </div>

            <div className="stat-grid">
              <WrappedStat
                title="Total revenue"
                tone="gold"
                value={cardMoney(wrapped.revenue_ex_cents)}
                hint="ex GST"
              />
              <WrappedStat
                title="Vs prior FY"
                tone={revenueChangeTone}
                value={
                  wrapped.revenue_change_pct === null
                    ? '—'
                    : `${wrapped.revenue_change_pct >= 0 ? '▲' : '▼'} ${Math.abs(wrapped.revenue_change_pct)}%`
                }
                hint={
                  wrapped.prior_fy === null ? (
                    'No prior FY to compare'
                  ) : (
                    <>
                      {fyShortLabel(wrapped.prior_fy)} was{' '}
                      <span className="pct">{cardMoney(wrapped.prior_revenue_ex_cents)}</span>
                    </>
                  )
                }
              />
              <WrappedStat
                title="Avg project value"
                tone="gold"
                value={
                  wrapped.avg_project_value_ex_cents === null ? '—' : cardMoney(wrapped.avg_project_value_ex_cents)
                }
                hint={`${wrapped.projects_delivered} project${wrapped.projects_delivered === 1 ? '' : 's'} delivered`}
              />
              <WrappedStat
                title="Lead → won · shoot → delivered"
                shortTitle="Lead → won"
                tone="accent"
                value={wrapped.avg_days_lead_to_won === null ? '—' : `${wrapped.avg_days_lead_to_won}d`}
                valueSuffix={wrapped.avg_days_lead_to_won === null ? undefined : 'avg'}
                hint={
                  wrapped.avg_days_shoot_to_delivered === null ? (
                    'shoot → delivered — no data'
                  ) : (
                    <>
                      shoot → delivered{' '}
                      <span className="pct">{wrapped.avg_days_shoot_to_delivered}d avg</span>
                    </>
                  )
                }
              />
            </div>

            <div className="two-up" style={{ marginTop: 24 }}>
              <div>
                <div className="group-head">
                  <span className="group-title">Revenue by client · top 5</span>
                </div>
                {wrapped.revenue_by_client.length > 0 ? (
                  <HBars
                    tone="gold"
                    items={wrapped.revenue_by_client.map((row) => ({
                      label: row.client_name,
                      value: row.revenue_ex_cents,
                      display: cardMoney(row.revenue_ex_cents),
                    }))}
                  />
                ) : (
                  <div className="empty-note">No revenue this FY yet.</div>
                )}
              </div>
              <div>
                <div className="group-head">
                  <span className="group-title">Busiest month</span>
                </div>
                <MonthBars
                  data={wrapped.months.map((m, i) => ({
                    label: monthLabel(m.month),
                    primary: m.count,
                    secondary: 0,
                    primaryTitle: `${monthLabel(m.month)} · ${m.count} project${m.count === 1 ? '' : 's'}`,
                    current: i === busiestIdx,
                    dim: i !== busiestIdx,
                  }))}
                  formatAxis={(n) => String(Math.round(n))}
                />
                <div className="group-head" style={{ marginTop: 16 }}>
                  <span className="group-title">Project count by type</span>
                </div>
                {wrapped.project_count_by_type.length > 0 ? (
                  <HBars items={wrapped.project_count_by_type.map((t) => ({ label: t.type, value: t.count }))} />
                ) : (
                  <div className="empty-note">No projects yet this FY.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {sendForm && <DocumentSendForm send={sendForm.send} onClose={() => setSendForm(null)} />}
    </>
  );
}

export function GoalCard({
  title,
  shortTitle,
  period,
  periodKey,
  today,
  tone,
  actual,
  actualDisplay,
  target,
  targetEditor,
  carriedFrom,
  onKeep,
}: {
  title: string;
  /** ≤880px eyebrow — "Revenue", not "Revenue this month": the group band
      above the grid already names the period (§11.1 A). */
  shortTitle?: string;
  period: string;
  /** The goal's period_key (e.g. '2026-07' / 'FY27 Q1') — drives the pace
      comparison below, distinct from `period`'s display label. */
  periodKey: string;
  today: string;
  tone: 'gold' | 'accent';
  actual: number;
  actualDisplay: string;
  target: number | null;
  targetEditor: React.ReactNode;
  carriedFrom?: string | null;
  onKeep?: () => Promise<void>;
}) {
  const isMobile = useIsMobile();
  const [keeping, setKeeping] = useState(false);
  const [keepError, setKeepError] = useState(false);
  const progress = target !== null && target > 0 ? Math.min(1, actual / target) : null;
  const over = target !== null && target > 0 && actual > target;
  const hit = target !== null && target > 0 && actual >= target;
  // Pace only has something to say before the target's hit — once it's
  // reached/passed the existing "target hit/passed" hint already covers it.
  const pace = target !== null && target > 0 && !hit ? paceOf(target, actual, periodKey, today) : null;
  const mobileHint = isMobile ? goalHintOf(actual, target, pace) : null;
  return (
    <div className={`goal-card${carriedFrom ? ' cf' : ''}${hit ? ` hit ${tone}` : ''}`}>
      <div className="goal-head">
        <span>{isMobile ? shortTitle ?? title : title}</span>
        <span className="period">{period}</span>
      </div>
      <div className="goal-actual">
        <span className={`now ${tone}`}>{actualDisplay}</span>
        <span className="of">
          of {targetEditor}
        </span>
      </div>
      <div className={`goal-bar${over ? ' over' : ''}`}>
        <span className={`fill-${tone}`} style={{ width: `${(progress ?? 0) * 100}%` }} />
      </div>
      <div className="goal-hint">
        {carriedFrom ? <span className="cf-hint">Carried forward from {carriedFrom.includes(' Q') ? carriedFrom : calendarMonthName(carriedFrom, true)} ·{' '}
          <button type="button" disabled={keeping} onClick={async () => {
            if (!onKeep) return;
            setKeeping(true); setKeepError(false);
            try { await onKeep(); } catch { setKeepError(true); } finally { setKeeping(false); }
          }}>{keeping ? 'Keeping…' : `Keep ${tone === 'gold' ? formatMoney(target) : target}`}</button>
          {keepError && <span role="alert"> Could not keep target. Try again.</span>}
        </span> : mobileHint ? (
          <span className={`gh-m ${mobileHint.tone}`}>{mobileHint.text}</span>
        ) : target === null || target === 0 ? (
          'No target set — click to add'
        ) : (
          <>
            <span className={`pct${pace && !pace.onPace ? ' behind' : ''}`}>
              {Math.round((actual / target) * 100)}% of target
              {hit ? ` · target ${over ? 'passed' : 'hit'}` : ''}
            </span>
            {pace && ` · ${pace.onPace ? 'on pace' : 'behind pace'}`}
          </>
        )}
      </div>
    </div>
  );
}

// EOFY wrapped's headline cards (mockup #9) — the same goal-card shell as
// GoalCard above, minus the progress bar/editor (these are read-only
// year-in-review figures, not editable targets).
function WrappedStat({
  title,
  shortTitle,
  tone,
  value,
  valueSuffix,
  hint,
}: {
  title: string;
  /** ≤880px eyebrow — a 176px card has no room for the long pairing, and the
      hint line underneath already carries the second half (§11.1 C). */
  shortTitle?: string;
  tone?: 'gold' | 'accent';
  value: React.ReactNode;
  valueSuffix?: React.ReactNode;
  hint: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  return (
    <div className="goal-card">
      <div className="goal-head">
        <span>{isMobile ? shortTitle ?? title : title}</span>
      </div>
      <div className="goal-actual">
        <span className={`now${tone ? ` ${tone}` : ''}`}>{value}</span>
        {valueSuffix && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-faint)' }}>
            {valueSuffix}
          </span>
        )}
      </div>
      <div className="goal-hint">{hint}</div>
    </div>
  );
}
